import * as React from 'react';

import {
  BookingStatus,
  enumBookingStatus,
} from '@rematter/frontend-lib-api-client';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

/**
 * Controls when the revert status/label should be shown for bookings:
 *   - 'always' (default): Show the revert action if the status logic matches
 *   - 'onlyIfVoidedOrComplete': Only show the revert action if the status is Voided or Completed
 *   - 'hideIfVoidedOrComplete': Never show the revert action if the status is Voided or Completed
 *   - 'hidden': Never show the revert action (always returns [null, null])
 */
export type RevertBookingVisibility =
  | 'always'
  | 'onlyIfVoidedOrComplete'
  | 'hideIfVoidedOrComplete'
  | 'hidden';

export type UseRevertBookingStatusParams = {
  status?: BookingStatus | null;
  visibility?: RevertBookingVisibility;
};

/**
 * Returns the next revert status and its i18n label key for a given booking status.
 *
 * @param params - See UseRevertBookingStatusParams
 * @returns [revertStatus, revertLabelKey] or [null, null] if not applicable
 */
export const useRevertBookingStatus = (
  params: UseRevertBookingStatusParams,
): [BookingStatus | null, string | null] => {
  const t = useTranslations();
  const { status, visibility = 'always' } = params;
  return React.useMemo(() => {
    if (visibility === 'hidden') {
      return [null, null];
    }
    if (
      visibility === 'onlyIfVoidedOrComplete' &&
      status !== enumBookingStatus.Voided &&
      status !== enumBookingStatus.Completed
    ) {
      return [null, null];
    }
    if (
      visibility === 'hideIfVoidedOrComplete' &&
      (status === enumBookingStatus.Voided ||
        status === enumBookingStatus.Completed)
    ) {
      return [null, null];
    }
    switch (status) {
      case enumBookingStatus.Completed:
        return [
          enumBookingStatus.Shipped,
          'bookingDetail.actions.revertToShipped',
        ];
      case enumBookingStatus.Shipped:
        return [enumBookingStatus.Open, t.bookingDetail.actions.revertToOpen];
      case enumBookingStatus.Voided:
        return [enumBookingStatus.Open, t.bookingDetail.actions.revertToOpen];
      default:
        return [null, null];
    }
  }, [status, t.bookingDetail.actions.revertToOpen, visibility]);
};
